import { CoreButtonView, CoreImageView, CoreTextView, type TCoreButtonStyleProps, type TCoreButtonViewProps, type TCoreTextStyleProps } from '@core/widgets';
import { TViewCommonOption, TWidgetCommonOption } from '@libs/types';
import React, { MouseEvent, MouseEventHandler } from 'react';
import { getSemanticColors, memoByPalette, SemanticColor, TSemanticColors } from '../../../theme/colors';
import { TRowOption } from '../../../types/options';
import { TextStyle } from '../../../types/styles';
import { getHref } from '../../Image/imageUtils';
import { getVisualIconMap } from '../../Image/styles/visualUtils';
import { VisualView } from '../../Image/VisualView';
import { Tag } from '../Tag/Tag';
import styles from './styles/selectorExtra.module.css';

export type TSelectorExtraViewProps = {
    /** Design version of the widget rendering the row (`TWidget.version`). */
    version: number | undefined;
    widget?: TCoreButtonViewProps['widget'];
    uid?: string;
    title?: string;
    subtitle?: string;
    metaText?: string;
    metaVariant?: TRowOption['metaVariant'];
    metaHierarchy?: TRowOption['metaHierarchy'];
    iconName?: string;
    defaultIconName?: string;
    flagIcon?: string;
    flagIconSmall?: string;
    avatar?: string;
    iconHierarchy?: TRowOption['iconHierarchy'];
    iconVariant?: TRowOption['iconVariant'];
    isSelected?: boolean;
    curIso?: string;
    curIsoIcon?: boolean;
    margin?: TWidgetCommonOption['margin'];
    isDisabled?: boolean;
    isHide?: boolean;
    handleClick: ((e: MouseEvent) => void) | MouseEventHandler<HTMLButtonElement>;
    parentStyleNames?: TViewCommonOption['parentStyleNames'];
};

const buildSelectorExtraStyleProps = (colors: TSemanticColors) => {
    const containerStyleProps: TCoreButtonStyleProps = {
        main: colors[SemanticColor.FillSecondary],
        onMain: colors[SemanticColor.TextPrimary],
        fillContainer: true,
        borderRadius: 6,
        cursorDisabled: 'default',
        textAlign: 'start',
        gap: 16,
        disabled: {
            main: colors[SemanticColor.FillRowDisabled],
            onMain: colors[SemanticColor.TextPrimaryDisabled],
        },
        hovered: {
            main: colors[SemanticColor.FillRowHovered],
        },
        pressed: {
            main: colors[SemanticColor.FillRowPressed],
        },
    };

    const titleStyleProps: TCoreTextStyleProps = {
        ...TextStyle.main,
        textAlign: 'start',
        main: colors[SemanticColor.TextPrimary],
        disabled: {
            main: colors[SemanticColor.TextPrimaryDisabled],
        },
    };

    const subtitleStyleProps: TCoreTextStyleProps = {
        ...TextStyle.secondary,
        textAlign: 'start',
        main: colors[SemanticColor.TextSecondary],
        margin: { top: 4 },
        disabled: {
            main: colors[SemanticColor.TextPrimaryDisabled],
        },
    };

    return { containerStyleProps, titleStyleProps, subtitleStyleProps };
};
const getSelectorExtraStyleProps = memoByPalette(buildSelectorExtraStyleProps);

export const SelectorExtraView = ({
    version,
    widget: resolvedWidget,
    uid: resolvedUid = '',
    title: resolvedTitle,
    subtitle: resolvedSubtitle,
    metaText: resolvedMetaText,
    metaVariant: resolvedMetaVariant,
    metaHierarchy: resolvedMetaHierarchy,
    iconName: resolvedIconName,
    defaultIconName,
    flagIcon: resolvedFlagIcon,
    flagIconSmall: resolvedFlagIconSmall,
    avatar: resolvedAvatar,
    iconHierarchy: resolvedIconHierarchy,
    iconVariant: resolvedIconVariant,
    isSelected: resolvedIsSelected,
    curIso,
    curIsoIcon,
    margin: resolvedMargin,
    isDisabled,
    isHide,
    handleClick,
    parentStyleNames = [],
}: TSelectorExtraViewProps) => {
    const semanticColors = getSemanticColors(version);
    const rowStyleProps = getSelectorExtraStyleProps(semanticColors);
    const useCurrencyVisual = !!(curIsoIcon && curIso);
    const hasGeneralVisual = !!(resolvedIconName || resolvedFlagIcon || resolvedAvatar);
    const hasVisual = useCurrencyVisual || hasGeneralVisual;

    const renderTrailingMeta = () => {
        if (!resolvedMetaText) {
            return null;
        }
        return (
            <Tag
                metaText={resolvedMetaText}
                metaVariant={resolvedMetaVariant}
                metaHierarchy={resolvedMetaHierarchy}
                isDisabled={isDisabled}
                parentStyleNames={parentStyleNames}
                styleName={resolvedMetaVariant === 'tag' ? 'selectorExtraTagText' : 'selectorExtraMeta'}
                version={version}
            />
        );
    };

    return (
        <CoreButtonView
            widget={resolvedWidget ?? ({ uid: resolvedUid, data: {}, options: {} } as TCoreButtonViewProps['widget'])}
            text={resolvedTitle}
            isDisabled={isDisabled}
            isHide={isHide}
            styleProps={{
                ...rowStyleProps.containerStyleProps,
                padding: { top: hasVisual ? 16 : 24, right: 24, bottom: hasVisual ? 16 : 24, left: 24 },
                margin: resolvedMargin,
            }}
            parentStyleNames={parentStyleNames}
            onClick={handleClick as MouseEventHandler<HTMLButtonElement>}
        >
            {hasVisual &&
                (useCurrencyVisual ? (
                    <VisualView
                        version={version}
                        uid={`icon-${resolvedUid}`}
                        iconName={`ic_${curIso?.toLowerCase()}`}
                        defaultIconName="ic_default_currency"
                        iconHierarchy="primary"
                        iconVariant="circle"
                        size="small"
                        isDisabled={isDisabled}
                        isSelected={resolvedIsSelected}
                        parentStyleNames={parentStyleNames}
                        styleName="selectorExtraCurrencyVisual"
                    />
                ) : (
                    <VisualView
                        version={version}
                        uid={`icon-${resolvedUid}`}
                        iconName={resolvedIconName}
                        defaultIconName={defaultIconName}
                        avatar={resolvedAvatar}
                        flagIcon={resolvedFlagIcon}
                        flagIconSmall={resolvedFlagIconSmall}
                        iconHierarchy={resolvedIconHierarchy}
                        iconVariant={resolvedIconVariant}
                        size="small"
                        isDisabled={isDisabled}
                        isSelected={resolvedIsSelected}
                        parentStyleNames={parentStyleNames}
                        styleName="selectorExtraVisual"
                    />
                ))}
            <div className={styles.rowContent}>
                {resolvedTitle && (
                    <CoreTextView
                        uid={'title-' + resolvedUid}
                        text={resolvedTitle}
                        as="span"
                        isDisabled={isDisabled}
                        parentStyleNames={parentStyleNames}
                        styleName="selectorExtraTitle"
                        styleProps={rowStyleProps.titleStyleProps}
                    />
                )}
                {resolvedSubtitle && (
                    <CoreTextView
                        uid={'subtitle-' + resolvedUid}
                        text={resolvedSubtitle}
                        as="span"
                        isDisabled={isDisabled}
                        parentStyleNames={parentStyleNames}
                        styleName="selectorExtraSubtitle"
                        styleProps={rowStyleProps.subtitleStyleProps}
                    />
                )}
            </div>
            {renderTrailingMeta()}
            {!hasVisual && resolvedIsSelected && (
                <CoreImageView
                    src={getHref('ic_check', version)}
                    isDisabled={isDisabled}
                    parentStyleNames={parentStyleNames}
                    styleName="selectorExtraCheck"
                    styleProps={{ width: 24, height: 24, ...getVisualIconMap(semanticColors).simple.emphasised }}
                />
            )}
        </CoreButtonView>
    );
};
